import { useDebounceFunc } from "client/hooks/utils/useDebounceFunc";
import axiosInstance from "client/lib/axiosInterceptor";
import { calculateCanvasImageDimensions } from "client/utils/calculateCanvasImageDimensions";
import { downloadImage } from "client/utils/downloadImage";
import { getFCUrl } from "client/utils/getFCUrl";
import { loadImage } from "client/utils/imageEdit/loadImage";
import { sizeToBytes } from "client/utils/sizeToBytes";
import { sleep } from "client/utils/sleep";
import { useState, useRef, useEffect, ChangeEvent } from "react";
import { useSelector } from "react-redux";
import { compressSelector } from "store/features/compress/selectors/compressSelector";
import { CompressImage, CompressionSettings, CompressState } from "store/features/compress/types";

const MIDDLE_VALUE = 50;

interface ImageLoadingStates {
    [id: string]: {
        isLoading: boolean;
        progress: number;
    };
}

interface ImageCache {
    original?: string;
    preview?: {
        src: string;
        settings: CompressionSettings;
    };
}
let imageCaches: Record<string, ImageCache> = {};

let currentImageId: string = "";

export const useCompressCompare = (image: CompressImage) => {
    const [value, setValue] = useState(MIDDLE_VALUE);
    const { selectedImageId } = useSelector(compressSelector, equalityFn);
    const sliderRangeRef = useRef<HTMLInputElement>(null);
    const [compressedSrc, setCompressedSrc] = useState("");

    const canvasRef = useRef<HTMLImageElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [canvasHeight, setCanvasHeight] = useState(0);

    currentImageId = image.id;

    // image loading states
    const [imageLoadingStates, setImageLoadingStates] = useState<ImageLoadingStates>({});
    const [isLoadingOriginalImage, setIsLoadingOriginalImage] = useState(false);
    const [originalImageLoadingPercentage, setOriginalImageLoadingPercentage] = useState(0);

    function startImageLoading(imageId: string) {
        setImageLoadingStates((state) => {
            // this is to prevent a glitch
            // not important to core logic
            if (!state[imageId]?.isLoading && state[imageId]?.progress === 100) {
                return state;
            }

            return {
                ...state,
                [imageId]: {
                    isLoading: true,
                    progress: 0,
                },
            };
        });
    }

    function endImageLoading(imageId: string) {
        setImageLoadingStates((state) => {
            return {
                ...state,
                [imageId]: {
                    isLoading: false,
                    progress: 100,
                },
            };
        });

        setTimeout(() => {
            setImageLoadingStates((state) => {
                return {
                    ...state,
                    [imageId]: {
                        isLoading: false,
                        progress: 0,
                    },
                };
            });
        }, 3000);
    }

    function setImageLoadingProgress(progress: number, imageId: string) {
        setImageLoadingStates((state) => {
            if (state[imageId]?.progress > progress || !state[image.id] || state[image.id]?.progress === 100)
                return state;

            return {
                ...state,
                [imageId]: {
                    isLoading: true,
                    progress,
                },
            };
        });
    }

    const drawImage = async () => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        setIsLoadingOriginalImage(true);
        const { width: containerWidth, height: containerHeight } = container.getBoundingClientRect();
        let imageId = image.id;
        setOriginalImageLoadingPercentage(0);
        let downloadedSrc = imageCaches[imageId]?.original;
        if (!downloadedSrc) {
            downloadedSrc = getFCUrl(
                (
                    await downloadImage(image.src, sizeToBytes(image.original.size), (_percentage) => {
                        if (imageId !== currentImageId) return;
                        setOriginalImageLoadingPercentage(_percentage);
                    })
                ).src,
            );
            imageCaches[imageId] = {
                ...(imageCaches[imageId] ?? {}),
                original: downloadedSrc,
            };
        }

        if (!imageCaches[imageId]) {
            imageCaches[imageId] = {
                original: downloadedSrc,
            };
        }

        const { image: img } = await loadImage(downloadedSrc);
        const { width, height } = calculateCanvasImageDimensions(
            img.width,
            img.height,
            containerWidth,
            containerHeight,
            false,
        );
        setCanvasHeight(height);
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        canvas.width = width;
        canvas.height = height;
        canvas.src = downloadedSrc;
        setIsLoadingOriginalImage(false);

        sliderRangeRef.current?.style.setProperty("--thumb-height", `${height}px`);
    };

    function loadCompressedImage(image: CompressImage) {
        if (!image.compressed || selectedImageId !== image.id) return;

        const imageSize = sizeToBytes(image.compressed.size) ?? 1;
        const imageId = image.id;
        const settings = image.compressionSettings;

        const imageCache = imageCaches[imageId];
        if (
            imageCache &&
            imageCache.preview &&
            JSON.stringify(imageCache.preview.settings) === JSON.stringify(settings)
        ) {
            setCompressedSrc(getFCUrl(imageCache.preview.src));
            endImageLoading(imageId);
            setIsLoadingOriginalImage(false);
            return;
        }

        startImageLoading(image.id);

        const controller = new AbortController();
        console.log("chuppu controller");
        axiosInstance
            .get(image.compressed.src, {
                onDownloadProgress: (progressEvent: ProgressEvent) => {
                    if (currentImageId !== imageId) {
                        controller.abort();
                        return;
                    }
                    const progress = Math.floor((progressEvent.loaded / imageSize) * 100);
                    setImageLoadingProgress(progress, imageId);
                },
                responseType: "blob",
                signal: controller.signal,
            })
            .then(async (response) => {
                if (currentImageId !== imageId) return;

                const reader = new FileReader();
                reader.readAsDataURL(response.data);
                reader.onload = function () {
                    const imageDataUrl = reader.result;
                    setCompressedSrc(getFCUrl(imageDataUrl?.toString() ?? ""));

                    imageCaches[imageId] = {
                        ...(imageCaches[imageId] ?? {}),
                        preview: {
                            src: imageDataUrl?.toString() ?? "",
                            settings,
                        },
                    };
                };

                // this delay is set because the image takes some milliseconds to load even after download completion
                // which causes the previous preview image to be shown for a small bit
                // issue number: IR-365
                await sleep(100);
                endImageLoading(imageId);
            })
            .catch(() => {
                endImageLoading(imageId);
            });
    }

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => setValue(+e.target.value);

    useEffect(() => {
        // reset the slider when image has changed
        image.compressed && setCompressedSrc(image.compressed.src);
        setValue(MIDDLE_VALUE);
    }, [image.id]);

    const debounce = useDebounceFunc();
    useEffect(() => {
        debounce(() => loadCompressedImage(image));
    }, [image.compressed?.src]);

    useEffect(() => {
        debounce(drawImage);
    }, [image.id, containerRef, canvasRef]);

    return {
        value,
        handleChange,
        sliderRangeRef,
        imageLoadingStates,
        canvasRef,
        containerRef,
        compressedSrc,
        isLoadingOriginalImage,
        originalImageLoadingPercentage,
        canvasHeight,
    };
};

const equalityFn = (left: CompressState, right: CompressState) => {
    return left.selectedImageId === right.selectedImageId;
};
